import React, {useEffect, useRef, useState} from "react";
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import {Button} from "primereact/button";
import {confirmPopup, ConfirmPopup} from "primereact/confirmpopup";
import {Toast} from "primereact/toast";
import {InputChangeEvent,} from "components/pages/scheduled-task-airflow/components/InputFields";
import {InputText} from "primereact/inputtext";
import {TaskInputGroup} from "../../../../../api/model/airflow-task/TaskInputGroup";
import {TaskTemplateInputGroupDefinition} from "../../../../../api/model/airflow-task/task-template/TaskTemplateInfo";

interface ITaskTemplatesSubgroupManagerTableProps {
    groupDefinition: TaskTemplateInputGroupDefinition
    subgroups: TaskInputGroup[]
    onChange: (e: InputChangeEvent<TaskInputGroup[]>) => void;
    changeTab: (title: string) => void
}

interface ISubgroupRowModel {
    value: TaskInputGroup,
    editValue: TaskInputGroup,
    editMode: boolean,
}

export default function TaskTemplatesSubgroupManagerTable(props: ITaskTemplatesSubgroupManagerTableProps) {
    const toast = useRef<Toast>(null);

    const subgroupHolder = mapToModel(props.subgroups)
    const [subgroups, setSubgroups] = useState([...subgroupHolder])

    const [searchText, setSearchText] = useState<string>("")

    useEffect(() => {
        setSubgroups(subgroupHolder.filter(val => val.value.title?.includes(searchText)))
    }, [searchText])

    const propagateChangesStateUp = (arr: ISubgroupRowModel[]) => {
        const tmp = {value: arr.map((e) => e.value!!)} as InputChangeEvent<TaskInputGroup[]>;
        props.onChange(tmp);
    }

    const confirmWrapper = (event: any, confirmHandler: any) => {
        confirmPopup({
            target: event.currentTarget,
            message: 'Are you really want to delete this subgroup?',
            icon: 'pi pi-exclamation-triangle',
            accept: confirmHandler,
        });
    }

    const showWarn = (warn: string) => {
        toast.current?.show({severity: 'warn', summary: 'Invalid', detail: warn, life: 3000});
    }

    const handleEdit = (row: ISubgroupRowModel) => {
        const idx = subgroups.indexOf(row)
        subgroups[idx].editMode = true
        subgroups[idx].editValue = subgroups[idx].value
        setSubgroups([...subgroups])
    }

    const handleEditCancel = (row: ISubgroupRowModel) => {
        const idx = subgroups.indexOf(row)

        if (!row.editValue.title || !row.value.title) {
            const newArr = [...subgroups]
            newArr.splice(idx, 1);
            setSubgroups(newArr);
            return;
        }

        subgroups[idx].editMode = false
        subgroups[idx].editValue = subgroups[idx].value
        setSubgroups([...subgroups])
    }

    const handleEditConfirm = (row: ISubgroupRowModel) => {
        const idx = subgroups.indexOf(row)

        if (!subgroups[idx].editValue.title) {
            showWarn("Subgroup title is required!")
            return
        }

        const duplicates = subgroups.filter((it) => it.editValue.title === row.editValue.title).length
        if (duplicates > 1) {
            showWarn("Title is already used!")
            return;
        }

        subgroups[idx].editMode = false
        subgroups[idx].value = subgroups[idx].editValue
        setSubgroups([...subgroups])
        propagateChangesStateUp([...subgroups])
    }

    const handleAdd = () => {
        const group = new TaskInputGroup({isGrouped: true})

        const updated = [{value: group, editValue: group, editMode: true} as ISubgroupRowModel, ...subgroups];
        setSubgroups(updated);
    }

    const handleDelete = (row: ISubgroupRowModel) => {
        const filtered = [...subgroups.filter(item => item.value !== row.value)];
        setSubgroups(filtered);
        propagateChangesStateUp(filtered)
    }

    const handleTitleChange = (e: any, row: ISubgroupRowModel) => {
        const idx = subgroups.indexOf(row)
        subgroups[idx].editValue.title = e.target.value
        setSubgroups([...subgroups])
    }

    const onRowReorder = (e: any) => {
        if (e.value.filter((e: any) => e.value.title == null).length !== 0) {
            toast.current?.show({detail: "Can't reorder while subgroup without the title", severity: "warn"})
            return
        }

        if (searchText.length !== 0) {
            toast.current?.show({detail: "Using search prevents reordering", severity: "warn"})
        }

        const updated = [...e.value]
        setSubgroups(updated)
        propagateChangesStateUp(updated)
    }

    const titleTemplate = (
        <div className="flex justify-content-between align-items-center">
            <div className="flex flex-row align-items-center gap-3">
                <div className="font-bold" style={{fontSize: "18px"}}>
                    Subgroups
                </div>
                <InputText placeholder="Search" value={searchText} onChange={(e) => setSearchText(e.target.value)}/>
            </div>
            <Button icon="pi pi-plus" className="p-button-success" onClick={() => handleAdd()}/>
        </div>
    );

    const enterPress = (e: React.KeyboardEvent<HTMLInputElement>, row: ISubgroupRowModel) => {
        if (e.code === "Enter" || e.code === "NumpadEnter") {
            handleEditConfirm(row)
        }
    }

    const titleBodyTemplate = (row: ISubgroupRowModel) => {
        return <React.Fragment>
            {!row.editMode &&
                <span>{row.value.title}</span>
            }

            {row.editMode &&
                <InputText placeholder="Title" value={row.editValue.title || ''}
                           onChange={e => handleTitleChange(e, row)} autoFocus onKeyDown={(e) => enterPress(e, row)}
                />
            }
        </React.Fragment>
    }

    const changeTab = (title?: string) => {
        title && props.changeTab(title)
    }

    const actionBodyTemplate = (row: ISubgroupRowModel) => {
        return <>
            {!row.editMode &&
                <Button icon="pi pi-reply" className="p-button-info p-button-rounded p-button-text"
                        onClick={() => changeTab(row.value.title)}/>
            }

            {!row.editMode &&
                <Button icon="pi pi-pencil" className="p-button-info p-button-rounded p-button-text"
                        onClick={() => handleEdit(row)}/>
            }

            {row.editMode &&
                <Button icon="pi pi-check" className="p-button-info p-button-rounded p-button-text"
                        onClick={() => handleEditConfirm(row)}/>
            }

            {row.editMode &&
                <Button icon="pi pi-times" className="p-button-plain p-button-rounded p-button-text"
                        onClick={() => handleEditCancel(row)}/>
            }

            <Button icon="pi pi-trash" className="const-del-btn  p-button-rounded p-button-text p-button-danger"
                    onClick={(e) => confirmWrapper(e, () => handleDelete(row))}/>
        </>
    }


    return <>
        <Toast ref={toast}></Toast>

        <DataTable value={subgroups}
                   responsiveLayout="scroll"
                   header={titleTemplate}
                   width={"100%"}
                   onRowReorder={onRowReorder}
                   size="small"
                   reorderableRows

                   paginator={searchText.length === 0}
                   paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
                   currentPageReportTemplate="Showing {first} to {last} of {totalRecords}" rows={7}
        >
            <Column rowReorder style={{width: '4em'}}/>
            <Column header="Title" body={titleBodyTemplate}/>
            <Column body={actionBodyTemplate}
                    bodyStyle={{textAlign: "right", width: "12rem"}}/>
        </DataTable>

        <ConfirmPopup/>
    </>
}

const mapToModel = (data: any[]) => {
    return data.map((it) => {
        return {value: it, editValue: it, editMode: false} as ISubgroupRowModel
    });
}